import appUtils from '@/utils/appUtils'
import lvmmedService from '@/services/lvmmedService'

const DEFAULT_EXTERNAL_USER_ID = 'hodo'
const DEFAULT_AGENT_TYPE = 'type_2.2'
const DEFAULT_HODO_API_ORIGIN = 'https://api.hodo.app'

function getAuthToken () {
  return appUtils.getLocalToken() || ''
}

function getBaseUrl () {
  return `${getHodoApiOrigin()}/api/go/lvmmed/proxy`
}

function trimTrailingSlash (value = '') {
  return String(value || '').replace(/\/+$/, '')
}

function getHodoApiOrigin () {
  return trimTrailingSlash(process.env.VUE_APP_HODO_API_ENDPOINT || process.env.VUE_APP_BASE_API || DEFAULT_HODO_API_ORIGIN)
}

function getAskByTypeUrl () {
  return `${getHodoApiOrigin()}/api/go/lvmmed/proxy/stream/ask-by-type`
}

function getExternalUserId () {
  const user = appUtils.getLocalUser() || {}
  const username = String(user?.username || '').trim()
  if (username) return username
  return process.env.VUE_APP_SUPERDOC_EXTERNAL_USER_ID || DEFAULT_EXTERNAL_USER_ID
}

function buildHeaders ({ accept = 'application/json', includeExternalOrgId = false } = {}) {
  const token = getAuthToken()
  const externalOrgId = includeExternalOrgId ? lvmmedService.getExternalOrgId() : ''
  return {
    Accept: accept,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-External-User-ID': getExternalUserId(),
    ...(externalOrgId ? { 'x-external-org-id': externalOrgId } : {})
  }
}

function toQueryString (params = {}) {
  const search = new URLSearchParams()
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value === undefined || value === null || value === '') return
    search.append(key, value)
  })
  const query = search.toString()
  return query ? `?${query}` : ''
}

async function parseJsonResponse (response) {
  let payload = null
  try {
    payload = await response.json()
  } catch (e) {}

  if (!response.ok) {
    const message = payload?.message || payload?.status || 'SuperDoc API error'
    throw new Error(message)
  }

  return payload
}

function mapStreamPayload (raw) {
  const normalizedRaw = String(raw || '').trim()
  if (!normalizedRaw || normalizedRaw === '[DONE]') {
    return { done: true, raw, mode: 'none', text: '' }
  }

  try {
    const parsed = JSON.parse(normalizedRaw)
    const payload = parsed?.data !== undefined && parsed?.data !== null ? parsed.data : parsed
    const status = payload?.status || payload?.type || parsed?.status || parsed?.type
    if (parsed?.done === true || payload?.done === true || status === 'done' || status === 'completed') {
      return { done: true, parsed, raw, mode: 'none', text: '' }
    }

    if (typeof payload === 'string') {
      return { done: false, parsed, raw, mode: 'append', text: payload }
    }

    if (typeof payload?.answer === 'string') {
      return { done: false, parsed, raw, mode: 'replace', text: payload.answer }
    }

    const appendText = payload?.delta || payload?.token || payload?.content || payload?.text || payload?.message || ''
    return { done: false, parsed, raw, mode: 'append', text: typeof appendText === 'string' ? appendText : '' }
  } catch (e) {
    return { done: false, parsed: null, raw, mode: 'append', text: normalizedRaw }
  }
}

function extractStreamEvents (chunkBuffer, flush = false) {
  const events = []
  const lines = chunkBuffer.split(/\r?\n/)
  const rest = flush ? '' : (lines.pop() || '')

  lines.forEach((line) => {
    const trimmedLine = line.trim()
    if (!trimmedLine || trimmedLine.startsWith(':')) return

    if (/^data:/.test(trimmedLine)) {
      events.push(trimmedLine.replace(/^data:\s?/, ''))
      return
    }

    // Also accept newline-delimited JSON/plain text returned by some proxies.
    if (!/^(event|id|retry):/.test(trimmedLine)) {
      events.push(trimmedLine)
    }
  })

  if (flush && rest.trim()) events.push(rest.trim())

  return { events, rest }
}

class SuperDocService {
  async request (path, options = {}) {
    const response = await fetch(`${getBaseUrl()}${path}`, options)
    return parseJsonResponse(response)
  }

  getHistory (params = {}) {
    const query = toQueryString(params)
    return this.request(`/history${query}`, {
      method: 'GET',
      headers: buildHeaders({ includeExternalOrgId: true })
    })
  }

  getSessionDetail (sessionId) {
    return this.request(`/history/${sessionId}`, {
      method: 'GET',
      headers: buildHeaders({ includeExternalOrgId: true })
    })
  }

  getSessionMessages (sessionId) {
    return this.request(`/history/${sessionId}/messages`, {
      method: 'GET',
      headers: buildHeaders({ includeExternalOrgId: true })
    })
  }

  async externalSyncUser (payload = {}) {
    const response = await fetch('https://api-superdoc.hodo.app/api/go/authen/external-sync', {
      method: 'POST',
      headers: {
        ...buildHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    })

    const parsed = await parseJsonResponse(response)
    const data = parsed?.data || {}

    return {
      is_new_user: !!data?.is_new_user,
      redirect_url: data?.redirect_url || '',
      access_token: data?.access_token || '',
      refresh_token: data?.refresh_token || ''
    }
  }

  async askStream ({
    lang = 'vi',
    question = '',
    agentType = DEFAULT_AGENT_TYPE,
    sessionId = null,
    metadata = '',
    files = [],
    signal,
    onChunk,
    onDone
  } = {}) {
    const formData = new FormData()
    formData.append('lang', lang)
    formData.append('question', question)
    formData.append('agent_type', agentType || DEFAULT_AGENT_TYPE)
    formData.append('session_id', sessionId ? String(sessionId) : '0')
    const externalOrgId = lvmmedService.getExternalOrgId()
    if (externalOrgId) {
      formData.append('org_id', String(externalOrgId))
    }
    if (metadata) {
      formData.append('metadata', metadata)
    }
    ;(files || []).forEach((file) => {
      if (!file) return
      formData.append('files', file)
    })

    const response = await fetch(getAskByTypeUrl(), {
      method: 'POST',
      headers: buildHeaders({ accept: 'text/event-stream', includeExternalOrgId: true }),
      body: formData,
      signal
    })

    if (!response.ok || !response.body) {
      let errorText = ''
      try {
        errorText = await response.text()
      } catch (e) {}
      throw new Error(errorText || 'SuperDoc stream error')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let isDone = false

    const emitEvents = (events) => {
      for (const rawEvent of events) {
        const parsedEvent = mapStreamPayload(rawEvent)
        if (parsedEvent.done) {
          isDone = true
          break
        }
        if (typeof onChunk === 'function' && parsedEvent.text) {
          onChunk(parsedEvent)
        }
      }
    }

    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const { events, rest } = extractStreamEvents(buffer)
      buffer = rest
      emitEvents(events)
      if (isDone) break
    }

    if (!isDone) {
      buffer += decoder.decode()
      const { events } = extractStreamEvents(buffer, true)
      emitEvents(events)
    }

    if (typeof onDone === 'function') onDone()
  }
}

export default new SuperDocService()
